import * as angular from 'angular';
import * as _ from 'lodash';

import template from './countVertical.tpl.html';
import {
  Counts,
  LineCounts,
  LineEquipmentItem,
  LineItem,
  LineJunctionItem,
  LineEquipmentDetail,
  LineEquipmentGroup,
  LineEquipmentDetailImpl
} from "../../models/counts/lineCounts";

import { Plant } from "../../models/navigation";
import { apsViewModels } from "../../shared/dto/APS.ViewModels";
import { apsEnums } from "../../shared/dto/aps.enums";
import { States } from "../../shared/config/constant.module";
import { apsDisplayEnums } from "../../shared/dto/enums";
import { StateParams } from "../../common";
import CountsServiceName, { CountHub, CountsService } from "../../services/counts/counts.service";
import UIUtilityServiceName, { EnumOption, UIUtilityService } from "../../services/utility/uiUtility.service";
import LineConfigsServiceName, { LineConfigsService } from "../../services/lineConfigs/lineConfigs.service";
import LoaderServiceName, { LoaderService } from "../../components/loader/loader.service";
import NavigationServiceName, { NavigationService } from "../../services/navigation/navigation.service";
import AuthDataServiceName, { AuthDataService } from "../../services/auth/authData.service";
import GroupRejectsServiceName, { GroupRejectsService } from "../../services/counts/groupRejects.service";
import EquipmentRejectsName, { openEquipmentRejects } from "./equipmentRejects.controller";
import * as Highcharts from 'highcharts/highstock';
import { IInterval, Interval } from '../../directives/interval.factory';
import countsHeader from './counts.header.directive';
import { IBtn, IBtnArrayOptions } from '../dashboard/btnarray.interfaces';
import { lineCountsLoaded, setUsePercentGobsChangedMethod, ICountControllerBase } from './count.controller';


const requires = [CountsServiceName, UIUtilityServiceName, LineConfigsServiceName, LoaderServiceName,
  NavigationServiceName, AuthDataServiceName, GroupRejectsServiceName, EquipmentRejectsName,
  countsHeader
];

function mapLineToRow(line: LineItem): CountRow {
  return {
    isLine: true,
    line: line,
    junction: null,
    cssClass: null
  };
}

function mapJunctionToRow(junction: LineJunctionItem): CountRow {
  return {
    isLine: false,
    line: null,
    junction: junction,
    cssClass: 'table' + (junction.inputs.length > junction.outputs.length
      ? junction.inputs.length : junction.outputs.length).toString()
  };
}

function mapModelToAreas(model: LineCounts): CountArea[] {
  const areas = [];
  let area: CountArea = {
    rows: _.sortBy(model.lines.filter(l => l.startJunctionId === null), r => r.number)
      .map(l => mapLineToRow(l))
  };

  while (area.rows.length > 0) {
    areas.push(area);

    const previousLines = area.rows.filter(l => l.isLine).map(l => l.line);
    const rows = [];
    area.rows.forEach(row => {
      // if row is junction, add all of the output lines
      if (!row.isLine) {
        _.sortBy(row.junction.outputs, l => l.number).forEach(line => {
          rows.push(mapLineToRow(line));
        });
        return;
      }

      const junction = row.line.endJunction;
      if (!junction) {
        return;
      }

      // do not add the junction if it is already in the list of rows
      if (_.some(rows, r => !r.isLine && r.junction === junction)) {
        return;
      }

      // if the end junction references lines not in the previous area, add the line instead
      if (!junction.inputs.every(l => _.some(previousLines, al => al.id === l.id))) {
        rows.push(mapLineToRow(row.line));
      }

      rows.push(mapJunctionToRow(junction));
    });

    area = { rows: rows };
  }
  return areas;
}

const sparklineChart = function () {
  return {
    restrict: 'E',
    template: '<div></div>',
    scope: {
      options: '='
    },
    link: function (scope: any, element: any, attributes: any) {

      const options = {
        title: {
          text: ''
        },
        credits: false,
        chart: {
          width: 60,
          height: 21,
          borderWidth: 0,
          margin: [2, 0, 2, 0],
          type: 'area',
          style: {
            overflow: 'visible'
          },
          skipClone: true
        },
        yAxis: {
          endOnTick: false,
          startOnTick: false,
          labels: {
            enabled: false
          },
          title: {
            text: null
          },
          tickPositions: [0],
          showEmpty: false,
        },
        xAxis: {
          labels: {
            enabled: false
          },
          title: {
            text: null
          },
          startOnTick: false,
          endOnTick: false,
          tickPositions: [],
          showEmpty: false,
        },
        legend: {
          enabled: false
        },
        tooltip: {
          pointFormat: '{point.y}',
          headerFormat: '',
          hideDelay: 0,
          outside: true,
          shared: true
        },
        plotOptions: {
          series: {
            animation: false,
            lineWidth: 1,
            shadow: false,
            states: {
              hover: {
                lineWidth: 1
              }
            },
            marker: {
              radius: 1,
              states: {
                hover: {
                  radius: 2
                }
              }
            },
            fillOpacity: 0.25
          },
          column: {
            negativeColor: '#910000',
            borderColor: 'silver'
          }
        },
        series: [],
        lang: {
          noData: "No Data"
        }
      };

      // Wait until finished loading, so that the dynamic id's will be ready
      angular.element(document).ready(function () {

        // Load any charts with data (first time)
        try {
          let jsonData = JSON.parse(attributes.data);
          if (jsonData.length === 0) jsonData = [0, 0, 0, 0];
          options.series = [{ data: jsonData }];
          const chart = new Highcharts.Chart(element[0], options as Highcharts.Options);
        } catch (e) {
          return false;
        }

        // watch the data attribute and if it changes, update the chart series
        scope.$watch(function () {
          return element.attr('data');
        }, function (newValue) {
          try {
            if (newValue !== null) {
              let jsonData = JSON.parse(attributes.data);
              if (jsonData.length === 0) jsonData = [0, 0, 0, 0];
              options.series = [{ data: jsonData }];
              const chart = new Highcharts.Chart(element[0], options as Highcharts.Options);
            }
          } catch (e) {
            return false;
          }
        });
      });
    }
  };
};

export interface ICountVerticalController extends ICountControllerBase {
  $stateParams: StateParams;
  $state: angular.ui.IStateService;
  interval: IInterval;
  areas: CountArea[];
  plant: Plant;
  shop: apsViewModels.ShopNavigation;
  hub: CountHub;
  updatedModel: LineCounts;
  updatedAreas: CountArea[];
  countDisplayIntervalOptions: EnumOption[];
  countsInterval: apsDisplayEnums.CountDisplayInterval;
  canDisableLine: boolean;
  cavities: apsViewModels.CavityCountSummary[];
  hasCavities: boolean;
  detailChartConfig: any;
  showCharts: boolean;
  showHelpWindow: boolean;
  helpText: string;
  navigationUrls: IBtnArrayOptions;
  blinkClass: string;
  toggleHelpWindow(): void;
  showHelp(helpScreen: string): void;
  viewRejectDetail(line: LineItem, equipment: LineEquipmentItem): void;
  viewGroupRejectDetail(equipmentGroup: LineEquipmentGroup): void;
  countsIntervalChanged(): void;
  getLossPercentClass(equipment: any): string;
  getEquipmentClass(equipment: any): string;
  getRowClass(equipment: any): string;
  calculateSpeed(gobsCount: Counts): number;
  toggleLineActive(line: LineItem): void;
}

export interface CountRow {
  isLine: boolean;
  line: LineItem;
  junction: LineJunctionItem;
  cssClass: string;
}

export interface CountArea {
  rows: CountRow[];
}

export interface RejectTolerances {
  rejectMinPercentage: number;
  rejectMaxPercentage: number;
}

function countsConfig($stateProvider: angular.ui.IStateProvider) {
  $stateProvider.state({
    name: States.COUNTS_VERTICAL,
    url: '/countsvertical',
    controller: 'CountsVerticalCtrl as vm',
    template: template,
    data: { pageTitle: 'Vertical Counts' }
  });
}

function countsVerticalCtrl(
  $stateParams: StateParams,
  countsService: CountsService,
  lineConfigsService: LineConfigsService,
  loaderService: LoaderService,
  navigationService: NavigationService,
  authDataService: AuthDataService,
  groupRejectsService: GroupRejectsService,
  uiUtilityService: UIUtilityService,
  $scope: angular.IScope,
  $modal: mm.foundation.modal,
  $state: angular.ui.IStateService,
  $interval: angular.IIntervalService): ICountVerticalController {
  let countsPromise;
  let equipmentRejectTolerances = {};
  function getDetailChartConfig() {
    return {
      chart: {
        alignTicks: true,
        zoomType: 'xy',
        styleMode: true,
      },
      legend: {
        enabled: false
      },
      credits: {
        enabled: false
      },
      title: {
        text: null
      },
      xAxis: {
        categories: ['', '', '', ''],
        labels: {
          enabled: false
        }
      },
      yAxis: {
        title: {
          text: null
        },
        labels: {
          enabled: true
        },
        allowDecimals: false
      },
      plotOptions: {
        series: {
          marker: {
            enabled: true
          },
          animation: false
        }
      },
      tooltip: {
        useHtml: true,
        pointFormat: '<b>{point.y}</b>',
      },
      series: [{
        type: 'spline',
        data: [],
        color: '#919391',
        name: ''
      }]
    };
  }

  const vm: ICountVerticalController = {
    $stateParams: $stateParams,
    $state: $state,
    countsService: countsService,
    interval: null,
    model: null,
    areas: [],
    plant: null,
    shop: null,
    hub: null,
    updatedModel: null,
    updatedAreas: null,
    canDisableLine: false,
    countDisplayIntervalOptions: uiUtilityService.getListOptionsFromEnum(apsDisplayEnums.CountDisplayInterval,
      null, null, o => o),
    countsInterval: authDataService.getCountInterval(),
    viewRejectDetail: null,
    viewGroupRejectDetail: null,
    calculateSpeed: null,
    countsIntervalChanged: null,
    getLossPercentClass: null,
    getEquipmentClass: null,
    getRowClass: null,
    toggleLineActive: null,
    cavities: [],
    hasCavities: false,
    detailChartConfig: getDetailChartConfig(),
    showCharts: true,
    showHelpWindow: false,
    toggleHelpWindow: null,
    showHelp: null,
    helpText: '',
		navigationUrls: {
			buttons: [
				{ 'name': 'Horizontal Layout', 'value': 'horizontal', 'selected': false },
				{ 'name': 'Vertical Layout', 'value': 'vertical', 'selected': true },
				{ 'name': 'Vertical Layout (v2)', 'value': 'vc2', 'selected': false },
			],
			select: (button: IBtn) => {
				let stateParams = {
					plantId: vm.$stateParams.plantId,
					shopId: vm.$stateParams.shopId,
					userId: vm.$stateParams.userId,
				};

				switch (button.value) {
					case "horizontal": $state.go(States.COUNTS_HORIZONTAL, stateParams); break;
					case "vertical": $state.go(States.COUNTS_VERTICAL, stateParams); break;
					case "vc2": $state.go(States.COUNTS_VERTICAL_V2, stateParams); break;
				}
			}
		},
    blinkClass: " blink-dark",
    usePercentGobsChanged: null,
  };

	setUsePercentGobsChangedMethod(vm);

  const shopId = $stateParams.shopId;

  function getDetailCounts(): void {
    _.forEach(vm.updatedAreas, updatedArea => {
      _.forEach(updatedArea.rows, updatedRow => {
        if (updatedRow.line === null) {
          return;
        }
        _.forEach(updatedRow.line.equipment, updatedEquip => {
          _.forEach(vm.areas, area => {
            _.forEach(area.rows, row => {
              if (row.line === null) {
                return;
              }
              _.forEach(row.line.equipment, equip => {
                if (equip.id === updatedEquip.id) {
                  equip.inspectCount.count = updatedEquip.inspectCount.count;

                  if (updatedEquip.rejectCount != null) {
                    equip.rejectCount.count = updatedEquip.rejectCount.count;
                    equip.rejectCount.percent = updatedEquip.rejectCount.percent;
                    equip.rejectCount.gobPercent = updatedEquip.rejectCount.gobPercent;
                  }

                  if (typeof equip.inspectCount !== 'undefined') {
                    if (typeof equip.rejectCount !== 'undefined') {
                      if (typeof equip.inspectCount.count !== 'undefined') {
                        if (typeof equip.rejectCount.count !== 'undefined') {
                          if (equip.inspectCount.count === 0 && equip.rejectCount.count === 0) {
                            let resetLineEquipDetail: LineEquipmentDetail[] = [];
                            equip.lineEquipmentDetail = resetLineEquipDetail;
                          }
                        }
                      }
                    }
                  }

                }

              });
            });
          });
        });
      });
    });
    _.forEach(vm.areas, area => {
      _.forEach(area.rows, row => {
        if (row.line === null) {
          return;
        }
        _.forEach(row.line.equipment, equip => {
          const detailPromise = countsService.getEquipmentCounts(shopId, vm.model, equip, vm.countsInterval)
            .then(counts => {

              vm.model.currentDetailCounts = counts;

              if (!Array.isArray(equip.lineEquipmentDetail) || !equip.lineEquipmentDetail.length) {
                equip.lineEquipmentDetail = [];
              }

              const updatedLineEquipDetail: LineEquipmentDetail[] = [];

              if (typeof counts.tabs[0] !== 'undefined' && typeof counts.tabs[0].outputRows !== 'undefined') {

                _.forEach(counts.tabs[0].outputRows, outputRow => {

                  if (outputRow !== null) {

                    if (outputRow.cells !== null && outputRow.cells[0].count != null) {
                      const detailCount = outputRow.cells[0].count;
                      if (detailCount.count > 0) {
                        updatedLineEquipDetail.push(new LineEquipmentDetailImpl(
                          outputRow.displayName,
                          detailCount
                        ));
                      }
                    }
                  }
                });

                _.forEach(updatedLineEquipDetail, updateRow => {
                  const rowToUpdate = _.find(equip.lineEquipmentDetail, { name: updateRow.name });
                  if (typeof rowToUpdate === 'undefined') {
                    equip.lineEquipmentDetail.push(updateRow);
                  } else {
                    rowToUpdate.actualCount = updateRow.actualCount;
                  }
                });

              } else if (typeof counts.details !== 'undefined') {

                _.forEach(counts.details, outputRow => {
                  updatedLineEquipDetail.push(new LineEquipmentDetailImpl(
                    outputRow.name,
                    outputRow.count
                  ));
                });

                _.forEach(updatedLineEquipDetail, updateRow => {
                  const rowToUpdate = _.find(equip.lineEquipmentDetail, { name: updateRow.name });
                  if (typeof rowToUpdate === 'undefined') {
                    equip.lineEquipmentDetail.push(updateRow);
                  } else {
                    rowToUpdate.actualCount = updateRow.actualCount;
                  }
                });
              }

            });
        });
      });
    });
  }

  function refreshCounts(): void {
    const plantShopsPromise = navigationService.getNavigation().then(navModel => {
      vm.plant = navModel.selectedPlant;
      vm.shop = navModel.selectedShop;
    });

   let countsByShears = true;

    countsPromise = plantShopsPromise.then(() =>
      countsService.getLineCounts(shopId, vm.plant, vm.countsInterval, vm.canDisableLine, false, countsByShears)
        .then(result => {
          vm.model = result;
          lineCountsLoaded(vm);
          vm.areas = mapModelToAreas(vm.model);
          getDetailCounts();

          let rejects = vm.model.shopConfig.jobRuns[0].machineRejectPercentages;
          if (!!rejects) { 
            vm.areas.forEach(area => {
              area.rows.forEach(row => {
                if (!row.isLine) { return; }

                row.line.equipment.forEach(equipment => {
                  let equipWithRejectPercentage = rejects.filter(item => item.selectedMachine === equipment.name );
                  if (equipWithRejectPercentage.length > 0) {
                      let tolerances: RejectTolerances = {
                        rejectMinPercentage: equipWithRejectPercentage[0].rejectedMinPercentage,
                        rejectMaxPercentage: equipWithRejectPercentage[0].rejectedMaxPercentage
                      };
                      equipmentRejectTolerances[equipment.name] = tolerances;
                  }
                });
              });
            });
          }


          for (let id in vm.model.equipment) {
            if (vm.model.equipment[id].type === apsEnums.LineEquipmentType.HEYE
              || vm.model.equipment[id].type === apsEnums.LineEquipmentType.CheckDetector
              || vm.model.equipment[id].type === apsEnums.LineEquipmentType.MX4
              || vm.model.equipment[id].type === apsEnums.LineEquipmentType.Mcal
              || vm.model.equipment[id].type === apsEnums.LineEquipmentType.Multi
            ) {
              vm.hasCavities = true;
              break;
            }
          }
        }));
  }

  function update() {
    refreshCounts();
    if (vm) {
      if (vm.model) vm.model.handleIntervalChange(vm.countsInterval);
      authDataService.setCountInterval(vm.countsInterval);
    }
  }

  vm.countsIntervalChanged = () => {
    update();
  };

  vm.canDisableLine = authDataService.isPlantOrAPSIOAdminForCurrentPlant();

  update();

	vm.getLossPercentClass = (equipment: any): string => {
    let rejectTolerance = equipmentRejectTolerances[equipment.name];
    if(equipment.rejectCount && !!rejectTolerance
		    && (	 Number(equipment.rejectCount.percent) > rejectTolerance.rejectMaxPercentage
              || Number(equipment.rejectCount.percent) < rejectTolerance.rejectMinPercentage) ) {
      return 'fail-percentage'; 
    }
    return 'counts-equipment-details';
  };

	vm.getEquipmentClass = (equip: LineEquipmentItem): string => {
   
    let rejectTolerance = equipmentRejectTolerances[equip.name];
    
		var equipmentClass = '';
    if(equip.rejectCount && !!rejectTolerance
      && (Number(equip.rejectCount.percent) > rejectTolerance.rejectMaxPercentage 
				|| Number(equip.rejectCount.percent) < rejectTolerance.rejectMinPercentage)) {
			equipmentClass = 'rejectBgVerticals';
			if(vm.countsInterval == apsDisplayEnums.CountDisplayInterval.CurrentHour
				|| vm.countsInterval == apsDisplayEnums.CountDisplayInterval.CurrentShift
				|| vm.countsInterval == apsDisplayEnums.CountDisplayInterval.RollingHour
				|| vm.countsInterval == apsDisplayEnums.CountDisplayInterval.LastTwentyMinutes) {
				equipmentClass = vm.blinkClass;
			}
		}
		return equipmentClass;
	};

	vm.getRowClass = (equipment: any): string => {
    if (!equipment) { return ''; }
    if (equipment.isDisabled) {	return 'equipment-disabled'; }
    let rejectTolerance = equipmentRejectTolerances[equipment.name];
    var equipmentClass = '';
    if(equipment.rejectCount && !!rejectTolerance
		    && (	 Number(equipment.rejectCount.percent) > rejectTolerance.rejectMaxPercentage
              || Number(equipment.rejectCount.percent) < rejectTolerance.rejectMinPercentage) ) {
      equipmentClass = 'rejectEquipVerticals';
    }
    return equipmentClass;
  };

  update();

  vm.toggleLineActive = (line: LineItem): void => {
    if (!vm.canDisableLine) {
      return;
    }

    let message = `Line ${line.name} is currently ${line.isDisabled ? 'disabled' : 'active'}. `
      + `Would you like to ${line.isDisabled ? 'activate' : 'disable'} it?`;
    let subject = `${line.isDisabled ? 'Activate' : 'Disable'} Line ${line.name}?`;

    uiUtilityService.confirm(message, subject, 'Yes', 'No').then(confirmed => {
      if (!confirmed) {
        return;
      }

      let promise = lineConfigsService.toggleLine({
        id: line.id,
        shopId: line.shopId,
        isDisabled: !line.isDisabled
      }).then(() => line.isDisabled = !line.isDisabled);
      loaderService.loadingPromise(promise);
    });
  };

  vm.viewRejectDetail = (line: LineItem, equipment: LineEquipmentItem): void => {
    let modalPromise: angular.IPromise<any> = countsPromise;
    if (equipment.hasTabs || (equipment.detailDataPoints && equipment.detailDataPoints.length > 0)) {
      modalPromise = countsService.getEquipmentCounts(shopId, vm.model, equipment, vm.countsInterval)
        .then(counts => {
          vm.model.currentDetailCounts = counts;
        });
      loaderService.loadingPromise(modalPromise);
    }
    modalPromise.then(() => {
      const result = openEquipmentRejects({
        equipment: equipment,
        line: line,
        plant: vm.plant,
        shopId: shopId,
        canDisable: vm.canDisableLine,
        detail: vm.model.currentDetailCounts,
        lineConfig: vm.model
      }, $modal);

      result.then(() => {
        if (vm.model.currentDetailCounts) {
          delete vm.model.currentDetailCounts;
        }
      });
    });
  };

  vm.viewGroupRejectDetail = (equipmentGroup: LineEquipmentGroup): void => {
    vm.model.currentGroup = equipmentGroup;
    groupRejectsService.showGroupRejects({
      equipmentGroup: equipmentGroup,
      shopId: vm.shop.id,
      lineCounts: vm.model,
      countsInterval: vm.countsInterval
    });
  };

  vm.toggleHelpWindow = (): void => {
		vm.showHelpWindow = !vm.showHelpWindow;
	}

  vm.showHelp = (helpScreen: string) => 
  {
    
    vm.helpText = getCountsHelpText(helpScreen);
		vm.toggleHelpWindow();
  }

	let updateBlinkClass = () => {
		if (vm.blinkClass.indexOf("light") > 0) {
			vm.blinkClass = " blink-dark-vert";
		} else {
			vm.blinkClass = " blink-light-vert";
		}
	}

	var blinkTimer: angular.IPromise<any>;
	let createBlinkTimer = () => {
		updateBlinkClass();
		if (blinkTimer) {
			$interval.cancel(blinkTimer);
		}
		blinkTimer = $interval(updateBlinkClass, 600);
	}

	$scope.$on("$destroy", function() {
        if (blinkTimer) {
						$interval.cancel(blinkTimer);
        }
    });

  
  const promise = countsPromise.then(() => {

    vm.interval = new Interval(update, $interval, 5 * 60 * 1000); // 5 mintues
    vm.interval.start();

    $scope.$on('$destroy', () => {
      if (vm.interval != null) {
        vm.interval.destroy();
        vm.interval = null;
      }
    });
    createBlinkTimer();
  });

  loaderService.loadingPromise(promise);

  return vm;
}

export default angular.module('aps.controller.countsVertical', requires)
  .config(countsConfig)
  .controller('CountsVerticalCtrl', countsVerticalCtrl)
  .directive('sparklineChart', sparklineChart)
  .name;

  function getCountsHelpText(helpScreen: string): string {
    let helptext = '';
  
    switch (helpScreen) {
      case 'EQUIPMENT COUNTS':
        helptext = `  
        <div class="header">
          Equipment Counts
        </div>         
        <div class="subheader">
          <u>How the Counts Work</u>:
        </div>
        <div class="detail">
          Below is a sample count object, this is sample data just for instructional purposes.

          <div style="width:50%">
            <table class="history-t no-padding bs-table bs-table-striped" style="width:30%">
              <thead>
                <tr>
                  <th class="text-center" colspan="4">X</th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td>SQR</td>
                  <td>11328</td>
                  <td>166</td>
                  <td class="fail-percentage">1.5%</td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
        <div class="detail">
          As you can see the count object is broken up into 4 different fields. Here is the definition of each:
        </div>  
        <div class="detail">
          <ol type = "1">
            <li class="detail">
              <b>Equipment Shorthand:</b> This is a shorthand abreviation of the type of equipment configured on the line.
              In this case this is a high speed squeezer. This is abreviated as SQR.
            </li>
            <li class="detail">
              <b>Inspect Count:</b> The number of bottles going into the machine.
              In APS the preference is to use a direct integration to the machine for this counter.
              However, there are instances that we are dependent on a photoeye before the inspection equipment 
              to provide the inspect count.
            </li>
            <li class="detail">
              <b>Reject Count:</b>
              The amount of bottles rejected by the piece of inspection equipment.
              For most equipment we have a direct integration and the equipment keeps counts of rejects.
              However, for some equipment integrations we need to calculated it by taking the difference between
              bottles in and bottles out.
              <br/>
              <span class="formula">Reject Count = Reject Count from Machine</span><br/>
              <span class="count">- OR -</span><br/>
              <span class="formula">Reject Count = Machine Bottles In - Machine Bottles Out</span><br/>
             </li>
            <li class="detail">
              <b>Reject Percent:</b>
              The percent of loss for each piece of equipment.
              This calculation is based off the equipment inspects vs. the rejects.
              <br/>
              <span class="formula">Reject Percent = Reject Count / Inspect Count</span><br/>

              On each job run, an acceptable percentage range can be set for each equipment type. If the equipment has a tolerance set
              and the current value is outside those parameters, this value is highlighted in 
              <span class="red">red</span> to help users quickly identify issues.
            </li>
          </ol>
        </div>
        <div class="subheader">
          <u>Equipment Details</u>:
        </div>   
        <div class="detail">
          Every piece of equipment has the ability to drill-down. 
          All the user has to do to access this data is to click on the peice of equipment.
          Depending on the type of integration some equipment will display more data than others.
        </div>
          `;
        break;
      case 'EQUIPMENT GROUP':
        helptext = `  
        <div class="header">
          EQUIPMENT COUNTS by CATEGORY against SHEARS
        </div>         
        <div class="subheader">
          <u>Metrics per Category</u>:
        </div>
        <div class="detail">
          This metric measures the total losses by equipment type against shear cuts.
          The metric definitions are as follows.
        </div>
        <div class="detail">
          <ol type = "1">
            <li class="detail">
              <b>REJECTS:</b> 
              The total amount of bottles rejected in each equipment category.
              This value is highlighted in <span class="red">red</span> to help users quickly identify losses.
            </li>
            <li class="detail">
              <b>REJECT:</b>
              The percent loss for each equipment categor.
              This value is highlighted in <span class="red">red</span> to help users quickly identify losses.
              <br/>
              <span class="formula">REJECT = 100 * (Rejects by Category/ Shears)</span>
            </li>
          </ol>
        </div>
          `;
        break;
      case 'SHOP KPI':
        helptext = `  
        <div class="header">
          Shop KPI's
        </div>         
        <div class="subheader">
          <u>KPI Metric Definitions</u>:
        </div>
        <div class="detail">
          The goal of these KPI's is to see how the shop is performing at each point along the line.
          We use each pulse from the shear cuts to calculate how many gobs are currently being produced.
        </div>
        <div class="detail">
          <ol type = "1">
            <li class="detail">
              <b>MACHINE SPEED:</b>
              How many bottles per minute the machine is producing.
              When available we use direct machine integration.
              Otherwise, we use the shear pulses to estimate the speed.
            </li>
            <li class="detail">
              <b>H/E Efficiency:</b> 
              The percent of bottles going into the lehr, against the shears.
              <br/>
              <span class="formula">H/E Efficiency = Lehr In Counter / Shears</span>
            </li><style>
            .cur-ptr {
              cursor: pointer;
            }
          
            .noColor {
              background-color: transparent;
            }
          
            .noPadding {
              padding: 0px;
            }
          
            .no-bg {
              background-color: transparent;
            }
          
            .aps-info {
              background-color: #1690CB;
              color: white;
            }
          
            .vc-count-name {
              white-space: nowrap;
            }
          
            .vc-bordered {
              border: solid 1px silver;
            }
          
            .vc-td-nm {
              text-align: left;
              height: 100%;
              vertical-align: middle;
            }
          
            .eq-box {
              border: solid 1px silver;
              display: inline-block;
              z-index: 4;
              height: 19px;
              padding: 0;
              margin-bottom: 0;
              margin-right: 2px;
              margin-left: 0;
              margin-top: 2px;
            }
          
            .eq-tb {
              background-color: transparent;
            }
          
            .eq-tb>tbody>tr>td,
            .eq-tb>thead>tr>th {
              border: none;
              text-align: center;
              vertical-align: middle;
            }
          
            .eq-td-idx {
              text-align: center;
            }
          
            .eq-col {
              height: 17px;
              z-index: 5;
              padding-top: 1px;
              font-weight: bold;
            }
          
            .eq-idx {
              width: 13px;
              text-align: center;
              background-color: #1690CB !important;
              color: white;
            }
          
            .eq-ict {
              width: 60px;
              text-align: right;
            }
          
            .eq-rct {
              width: 50px;
              text-align: right;
            }
          
            .eq-rpt {
              width: 50px;
              text-align: center;
              padding-bottom:1px;
            }
            .eq-sparkline {
              width: 60px;
              background-color: transparent;
              z-index: 1002;
              text-align: center;
              height: 18px;
            }
          
            .eq-td-nm {
              width: 195px;
              white-space: nowrap;
              text-overflow: ellipsis;
            }
          
            .vc-header {
              height: 24px;
            }
          
            .vc-text-white {
              color: white;
            }
          
            .vc-bg-warning {
              background-color: yellow !important;
            }
          
            .vc-bg-err {
              background-color: red !important;
            }
          
            .ag-eq-rows>div[data-type='row']:nth-of-type(odd) {
              background-color: #dedddd;
            }
          
            .ag-eq-rows>div[data-type='row']:nth-of-type(even) {
              background-color: #F2F2F2;
            }
          
            .ag-eq-rows>div[data-type='header'] {
              background-color: #1690CB !important;
              color: white;
              font-weight: bold;
            }
          
            .vc-col,
            .eq-box>table>tbody>tr>td {
              font-size: 13.6px;
              font-weight: 400;
              font-style: normal;
              font-family: "Helvetica Neue", Helvetica, Arial, sans-serif;
              letter-spacing: 0.8px;
            }
          
            .vc-trend {
              margin-left: -4px;
              width: 9px;
              height: 14px;
            }
          
            .vc-col-th {
              margin-top: 3px;
              text-align: center;
            }
          
            .bs-btn-unselected {
              color: #000000;
              background-color: #e7e7e7;
              border-color: #b9b9b9;
            }
          
            .bs-btn-unselected:hover {
              background-color: #dedddd;
            }
          
            .bs-btn-selected {
              color: #fff;
              background-color: #008CBA;
              border-color: #007095;
            }
          
            .bs-btn-selected:hover {
              color: #fff;
              background-color: #007095;
              border-color: #007095;
            }
          
            .bs-btn {
              margin-bottom: 2px;
            }
          
            .bs-row {
              margin-left: 0;
              margin-right: 0;
            }
          
            .bs-card-equipmentbox {
              margin: 4px;
              cursor: pointer;
            }
          
            .bs-card-equipmentbox>.bs-card-body {
              padding: 8px;
            }
          
            .bs-card-kpi {
              margin: 4px;
              cursor: pointer;
            }
          
            .bs-card-kpi>.bs-card-body {
              padding: 10px;
            }
          
            .ag-toolbar {
              margin: 0;
              padding: 0 0 0 0;
              vertical-align: middle;
              display: inline-flex;
            }
          
            button,
            select {
              margin: 0;
            }
          
            .ag-plant-info {
              display: inline-block;
              padding: 6px 6px 6px 0;
              margin: 0;
            }
          
            .ag-interval-info {
              padding: 5px 5px 0 5px;
              margin: 0 0 0 0;
              display: inline-block;
            }
          
            .ag-interval-info>span {
              font-size: 14px;
              font-weight: 400;
            }
          
            .ag-buttonarray-info {
              padding: 3px 3px 0px 3px;
              margin: 0;
              display: inline-block;
            }
          
            .ag-plant-label {
              color: #1178A8;
              font-size: 1em;
              font-weight: 500;
              display: block;
            }
          
            .ag-shop-label {
              font-weight: 400;
              font-size: .75em;
              color: #000000;
              display: block;
            }
          
            .ag-header {
              padding: 0 0 0 0;
              margin-top: 6px;
            }
          </style>
          
          <!-- header -->
          <div class="row-flex ag-header">
            <div class="bs-col-12">
              <div class="ag-toolbar">
                <div class="ag-plant-info">
                  <span class="ag-plant-label">{{vm.plant.name || "..."}}</span>
                  <span class="ag-shop-label">Shop {{vm.shop.name}}</span>
                </div>
                <div class="ag-interval-info">
                  <span>Viewing Count Data For:</span>
                  <select style="width: 120px;" id="countsInterval" ng-model="vm.countsInterval"
                    ng-options="t.value as t.text for t in vm.countDisplayIntervalOptions"
                    ng-change="vm.countsIntervalChanged()" class="form-control"></select>
                </div>
                <div class="ag-buttonarray-info">
                  <btn-array options="vm.navigationUrls" />
                </div>
              </div>
            </div>
          </div>
          
          <!-- use bootstrap columns instead of tables -->
          <div class="row-flex">
            <div class="bs-col-12">
              <h6>
                <b>EQUIPMENT COUNTS (% INSPECT VS. REJECT)</b>&nbsp;<a ng-click="vm.showHelp('EQUIPMENT COUNTS')"><i class="fa fa-info-circle fa-bigger"></i></a>
              </h6>
              <div class="ag-eq-rows">
                <div class="row-flex vc-header" data-type="header">
                  <div class="bs-col-3">
                    <div class="vc-col-th">
                      <span>Device</span>
                    </div>
                  </div>
                  <div class="bs-col-9">
                    <div class="vc-col-th">
                      <span>Split Line Counters</span>
                    </div>
                  </div>
                </div>
          
                <!-- data -->
                <div class="row-flex" data-type="row" ng-repeat-start="count in vm.vc">
                  <div class="bs-col-3 no-margin">
                    <div class="vc-col cur-ptr" style="margin-top: 4px;" ng-click="vm.viewDetails(count,'parent')">
                      <span class="bs-text-nowrap count" title="{{count.id}}"  ng-class="vm.getEquipmentClass(count.equipment)">{{count.name}}</span>
                    </div>
                  </div>
                  <div class="bs-col-9 no-margin">
                    <div class="vc-col">		
                      <equipment-box equipment="equipment"
                                     options="vm.detailChartConfig"
                                     lossPercentClass={{vm.getLossPercentClass(equipment)}}
                                     borderClass={{vm.getRowClass(equipment)}}
                          class="cur-ptr" title="rejectCountId: {{equipment.rejectCount.id}}"
                        ng-repeat="equipment in count.equipment | orderBy: 'line.name'"
                        ng-click="vm.viewRejectDetail(count)"> {{vm.getEquipmentClass(equipment)}}</equipment-box>
                    </div>
                  </div>
                </div>
          
                <!-- children -->
                <div class="row-flex" data-type="row" ng-repeat="ct in count.children | orderBy:'name'">
                  <div class="bs-col-3">
                    <div class="vc-col cur-ptr" style="margin-left: 20px; margin-top: 4px;" ng-click="vm.viewDetails(ct,'child')">
                      <span class="" title="{{ct.id}}">
                        <b>{{ct.name}}</b>
                      </span>
                    </div>
                  </div>
                  <div class="bs-col-9">
                    <div class="vc-col">
                      <equipment-box equipment="eq" options="vm.detailChartConfig"
                        class="cur-ptr" title="{{eq.id}}"
                        ng-repeat="eq in ct.equipment | orderBy: 'line.name'" ng-click="vm.viewRejectDetail(ct)">
                      </equipment-box>
                    </div>
                  </div>
                </div>
          
                <div ng-repeat-end data-type="row" ng-if="false"></div>
              </div>
            </div>
          </div>
          <br/>
          <!-- equipment groups -->
          <div class="row-flex">
            
            <div class="bs-col-12">
              <h6>
                <b>EQUIPMENT COUNTS by CATEGORY (% OF SHEARS)</b>&nbsp;<a ng-click="vm.showHelp('EQUIPMENT GROUP')"><i class="fa fa-info-circle fa-bigger"></i></a>
              </h6>
              <div ng-repeat="group in vm.model.equipmentGroups" style="display: inline-block;"
                ng-click="vm.viewGroupRejectDetail(group)">
                <div class="bs-card bs-card-equipmentbox"  ng-class="vm.getRowClass(group)">
                  <div class="bs-card-body">
                    <div class="bs-card-title">
                      <b>{{group.name}}</b>
                    </div>
                    <div class="bs-card-text">
                      <div class="bs-text-right">
                        <b>{{group.inspectCount.count | number}}</b>
                      </div>
                    </div>
                    <div class="bs-card-text">
                      <div class="bs-text-right" style="margin-top: 0.3125em;">
                        <b>{{(group.rejectCount.count | number) || "-"}}</b>
                      </div>
                    </div>
                    <div class="bs-card-text">
                      <div class="bs-text-right" ng-class="vm.getLossPercentClass(group)"> 
                          <b>{{(group.rejectCount.getPercent() || "0") + "%"}}</b>
                          <vert-trend trend="group.rejectCount.trend" style="margin-top: .1em" />
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
          <br/>
          
          <!-- kpi -->
          <div class="bs-row">
            <div class="bs-col-12">
              <h6>
                <b>SHOP KPI (% INSPECT VS. REJECT)</b>&nbsp;<a ng-click="vm.showHelp('SHOP KPI')"><i class="fa fa-info-circle fa-bigger"></i></a>
              </h6>
              <div ng-repeat="kpi in vm.model.kpis" style="display: inline-block;">
                <div class="bs-card bs-card-kpi">
                  <div class="bs-card-body">
                    <div class="bs-card-text">
                      <label style="display: inline;">{{kpi.efficiencyName}}:</label>
                      <span>{{kpi.efficiency}}</span>
                    </div>
                    <div class="bs-card-text">
                      <span>
                        <label style="display: inline;">{{kpi.lossesName}}:</label>
                        <span>{{kpi.losses | number}}</span>
                      </span>
                    </div>
                    <div class="bs-card-text">
                      <span ng-if="$index === 0">
                        <label style="display: inline;">Speed:</label>
                        <span>{{kpi.denominator.speed}}</span>
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
          <div ng-if="vm.showHelpWindow" class="charthelpdetails">
            <div class="windowclose">
              <a><i class="fa fa-times-circle fa-bigger" ng-click="vm.toggleHelpWindow()"></i></a>
            </div>
            <div class="tooltiptext" ng-bind-html="vm.helpText">
            </div>
          </div>
            <li class="detail">
              <b>C/E Efficiency:</b>
              The percent of bottles that make it to the End of Line (EOL), compared to the lehr. 
              This is usually a photoeye, sometimes it can be the last piece of inspection equipment. It depends on the shop configuration.
              <br/>
              <span class="formula">C/E Efficiency = EOL / Lehr In Counter</span>
            </li>
            <li class="detail">
              <b>Pack Efficiency:</b>
              The percent of bottles that make it to the End of Line (EOL), against the shears. 
              This is usually a photoeye, sometimes it can be the last piece of inspection equipment. It depends on the shop configuration.
              <br/>
              <span class="formula">Pack Efficiency = EOL / Shears</span>
            </li>
          </ol>
        </div>
          `;
        break;
    
      default:
        helptext = 'Selected Help Screen Not Found...';
        break;
    }
  
    return helptext;
  }


// WEBPACK FOOTER //
// ./src/app/controllers/counts/countVertical.controller.ts